import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginForm } from '@odo/shared-ui/login/LoginForm';
import { postLoginRedirect } from '@odo/shared-ui/login/postLoginRedirect';
import api, { AUTH_TOKEN_STORAGE_KEY } from '../api/config';
import { armPersistence } from '../providers/queryClient';
import logo from '../assets/icons/odo.png';
import { useUser } from '../providers/UserProvider';

function LoginErrorAlert({ onDismiss }: { onDismiss: () => void }) {
    return (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">Unable to sign in</h3>
                    <div className="mt-1 text-sm text-red-700">
                        <p>
                            The email or password you entered is incorrect. Please try again or
                            reset your password.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="ml-3 flex-shrink-0 inline-flex text-red-400 hover:text-red-600 transition-colors duration-200"
                >
                    <span className="sr-only">Dismiss</span>
                    <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

const Login: React.FC = () => {
    const { login } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const redirectTo = useMemo(
        () => postLoginRedirect(location.state, location.search),
        [location.state, location.search]
    );

    useEffect(() => {
        const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
        if (token) {
            navigate(redirectTo, { replace: true });
        }
    }, [navigate, redirectTo]);

    const handleLogin = async (email: string, password: string) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await api.post('/user/login', { email, password });
            if (response.status === 200) {
                localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, response.data.token);
                // Login navigates client-side, so persistence must be re-bound
                // here or this session would keep writing to the 'anon' bucket.
                armPersistence();
                const verifyResponse = await api.get('/user/verify');
                if ([200, 201].includes(verifyResponse.status)) {
                    login(verifyResponse.data.user);
                    navigate(redirectTo, { replace: true });
                } else {
                    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
                    armPersistence();
                    navigate('/login', { replace: true });
                }
            }
        } catch {
            setError('Invalid email or password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LoginForm
            logoSrc={logo}
            title="Sign in to your account"
            alert={error && <LoginErrorAlert onDismiss={() => setError('')} />}
            credentialsInvalid={error !== ''}
            isLoading={isLoading}
            showPasswordReset
            onCredentialsChange={() => setError('')}
            onSubmit={handleLogin}
        />
    );
};

export { Login };
